import { useEffect, useState } from "react";
import axios from "axios";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Coins, Phone, User, Calendar, CheckCircle, Zap, Clock, XCircle, AlertCircle, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const AdminCryptoRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/crypto/admin/requests`);
            setRequests(response.data);
        } catch (error) {
            console.error("Error fetching Crypto requests:", error);
            toast.error("Pa kapab chaje demann Crypto yo.");
        } finally {
            setLoading(false);
        }
    };

    const markCompleted = async (requestId) => {
        const hash = prompt("Antre TX Hash transaksyon USDT a:");
        if (!hash) return;
        try {
            setActionLoading(requestId);
            await axios.post(`${API}/crypto/admin/requests/${requestId}/mark-completed`, { tx_hash: hash });
            toast.success("USDT make kòm voye!");
            fetchRequests();
        } catch (error) {
            toast.error("Erè nan make kòm konplè.");
        } finally {
            setActionLoading(null);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('fr-HT', {
            style: 'decimal',
            minimumFractionDigits: 0,
        }).format(price);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString('fr-HT', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Kopye!");
    };

    const getStatusBadge = (status) => {
        const st = {
            "pending_payment": { label: "Atant Peman", class: "bg-amber-500 text-white", icon: Clock },
            "payment_received": { label: "Peye - Pou Voye USDT", class: "bg-blue-600 text-white shadow-lg shadow-blue-200", icon: Zap },
            "completed": { label: "Konplete", class: "bg-green-600 text-white", icon: CheckCircle },
            "cancelled": { label: "Anile", class: "bg-red-500 text-white", icon: XCircle }
        };
        const config = st[status] || { label: status, class: "bg-gray-500 text-white", icon: AlertCircle };
        return (
            <span className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 ${config.class}`}>
                <config.icon className="w-3 h-3" />
                {config.label}
            </span>
        );
    };

    return (
        <AdminLayout>
            <div className="space-y-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-black text-[#232F3E] tracking-tighter italic uppercase leading-none">
                            Vant <span className="text-teal-600">USDT TRC20</span>
                        </h1>
                        <p className="text-gray-400 font-bold uppercase tracking-widest text-[9px] mt-2">
                            Jere tout demann achte USDT kliyan yo.
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-24">
                        <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <Card className="border-0 shadow-2xl rounded-[35px] overflow-hidden bg-white">
                        <CardContent className="p-0">
                            {requests.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                                                <th className="py-6 px-10 text-left">Kliyan / Nimewo</th>
                                                <th className="py-6 px-8 text-left">Wallet TRC20</th>
                                                <th className="py-6 px-8 text-left">Kantite USDT</th>
                                                <th className="py-6 px-8 text-left">Total HTG</th>
                                                <th className="py-6 px-8 text-left">Estati</th>
                                                <th className="py-6 px-8 text-left">Dat</th>
                                                <th className="py-6 px-8 text-center text-[#FF9900]">Aksyon</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {requests.map((req) => (
                                                <tr key={req.id} className="hover:bg-gray-50/80 transition-all border-b border-gray-50 last:border-0">
                                                    <td className="py-6 px-10">
                                                        <div className="flex flex-col">
                                                            <span className="font-black text-[#232F3E] text-sm tracking-tight">{req.client_name}</span>
                                                            <span className="text-[10px] text-gray-500 font-bold">{req.client_phone}</span>
                                                            <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-1">REF: {req.request_number}</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-6 px-8">
                                                        <div className="flex items-center gap-2 group">
                                                            <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-700 truncate max-w-[150px]" title={req.wallet_address}>
                                                                {req.wallet_address}
                                                            </span>
                                                            <button onClick={() => copyToClipboard(req.wallet_address)} className="p-1 hover:bg-gray-200 rounded transition-colors opacity-0 group-hover:opacity-100">
                                                                <Copy className="w-3 h-3 text-gray-500" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td className="py-6 px-8">
                                                        <span className="font-black text-lg text-teal-700">{req.amount_usd.toFixed(2)} USDT</span>
                                                    </td>
                                                    <td className="py-6 px-8">
                                                        <div className="flex flex-col">
                                                            <span className="font-black text-emerald-600">{formatPrice(req.total_htg)} HTG</span>
                                                            <span className="text-[9px] text-gray-400">@ {req.exchange_rate}</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-6 px-8">
                                                        {getStatusBadge(req.status)}
                                                        {req.tx_hash && (
                                                            <div className="mt-1 text-[8px] font-mono text-gray-400 truncate max-w-[100px]" title={req.tx_hash}>
                                                                TX: {req.tx_hash}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="py-6 px-8 text-[11px] font-bold text-gray-400 uppercase">
                                                        {formatDate(req.created_at)}
                                                    </td>
                                                    <td className="py-6 px-8">
                                                        <div className="flex items-center justify-center gap-2">
                                                            {req.status === "payment_received" && (
                                                                <Button 
                                                                    size="sm" 
                                                                    onClick={() => markCompleted(req.id)}
                                                                    className="bg-teal-600 hover:bg-teal-700 text-white text-[10px] font-black uppercase rounded-xl h-9 px-4 shadow-lg shadow-teal-100"
                                                                    disabled={actionLoading === req.id}
                                                                >
                                                                    <Zap className="w-3 h-3 mr-1" /> Voye USDT
                                                                </Button>
                                                            )}
                                                            {req.status === "completed" && (
                                                                <div className="text-[10px] font-black text-green-600 uppercase">
                                                                    Voye ✅
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-32 text-gray-400">
                                    <Coins className="w-20 h-20 mx-auto mb-6 opacity-10" />
                                    <p className="font-black uppercase tracking-widest text-[11px]">Pa gen okenn demann Crypto ankò.</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>
        </AdminLayout>
    );
};
